import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaSearch, FaFilter, FaGamepad, FaGift, FaTimes } from 'react-icons/fa';
import { theme } from '../../styles/theme';
import { useAuth } from '../../hooks/useApi';
import ProductService from '../../services/ProductService';
import ApiService from '../../services/ApiService';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const sparkle = keyframes`
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;


// Styled Components
const CatalogContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  background: ${theme.gradients.background};
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 80%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(0, 0, 0, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 40% 40%, rgba(0, 0, 0, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 2% 4%, rgba(255, 255, 255, 0.26) 1px, transparent 0),
                      radial-gradient(circle at 29% 36%, rgba(255, 255, 255, 0.32) 1px, transparent 0),
                      radial-gradient(circle at 50% 60%, rgba(255, 255, 255, 0.2) 1px, transparent 0),
                      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.66) 1px, transparent 0),
                      radial-gradient(circle at 70% 10%, rgba(255, 255, 255, 0.11) 1px, transparent 0),
                      radial-gradient(circle at 60% 12%, rgba(255, 255, 255, 0.29) 1px, transparent 0),
                      radial-gradient(circle at 43% 90%, rgba(255, 255, 255, 0.51) 1px, transparent 0),
                      radial-gradient(circle at 32% 83%, rgba(255, 255, 255, 0.16) 1px, transparent 0),
                      radial-gradient(circle at 49% 53%, rgba(255, 255, 255, 0.6) 1px, transparent 0),
                      radial-gradient(circle at 89% 52%, rgba(255, 255, 255, 0.14) 1px, transparent 0);
    background-size: 25% 25%;
    pointer-events: none;
    background-repeat: repeat;
    animation: ${sparkle} 4s ease-in-out infinite;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1600px;
  width: 100%;
  margin: 0 auto;
  padding: ${theme.spacing.xl};
  position: relative;
  z-index: 1;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    padding: ${theme.spacing.lg};
  }

  @media (max-width: 768px) {
    padding: ${theme.spacing.md};
  }

  @media (max-width: 480px) {
    padding: ${theme.spacing.sm};
  }
`;

const FilterBarContainer = styled.div`
  margin-bottom: ${theme.spacing.xxl};
  animation: ${fadeIn} 0.8s ease-out 0.2s both;
  background: rgba(255, 255, 255, 0.03);
  padding: ${theme.spacing.lg};
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);

  @media (max-width: 768px) {
    margin-bottom: ${theme.spacing.xl};
    padding: ${theme.spacing.md};
  }
`;

const MainControls = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: center;
  margin-bottom: ${theme.spacing.lg};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 10px 14px;
  gap: 10px;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.02);

  .search-icon {
    color: rgba(255, 255, 255, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const SearchBar = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-size: 0.95rem;
  font-weight: 500;
  min-width: 0;

  &::placeholder {
    color: rgba(255, 255, 255, 0.45);
  }
`;

const SortSelect = styled.select`
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #ffffff;
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  min-width: 180px;
  cursor: pointer;

  option {
    color: #000000;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CategoryScroller = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 6px;
  }
`;

const FilterButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(0, 0, 0, 0.35);
  color: rgba(255, 255, 255, 0.85);
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  white-space: nowrap;

  &.active {
    background: ${theme.gradients.primary};
    color: #000000;
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
  }

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing.xl};
  margin-bottom: ${theme.spacing.xl};
  padding-left: 40px;
  padding-right: 40px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 1400px) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${theme.spacing.xl};
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacing.xl};
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacing.xl};
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacing.xl};
  }
  
  @media (max-width: 360px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${theme.spacing.xl};
  }
`;

const ProductCard = styled.div`
  background: linear-gradient(180deg, rgba(30, 30, 60, 0.95) 0%, rgba(15, 15, 35, 0.98) 100%);
  border: 3px solid rgba(118, 75, 162, 0.5);
  border-radius: 16px;
  padding: 0;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  aspect-ratio: 3/4;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 
    0 8px 24px rgba(0, 0, 0, 0.6),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  will-change: transform;

  @media (max-width: 768px) {
    border-width: 2px;
    border-radius: 12px;
  }

  @media (max-width: 480px) {
    border-radius: 10px;
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 
      0 16px 40px rgba(102, 126, 234, 0.6),
      0 0 30px rgba(118, 75, 162, 0.4);
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const ProductImage = styled.div`
  width: 100%;
  flex: 1;
  background: ${props => props.$hasImage 
    ? 'linear-gradient(180deg, #1a1a3e 0%, #0f0f2a 100%)' 
    : 'linear-gradient(180deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%)'};
  border-radius: 12px 12px 0 0;
  display: flex;
  position: relative;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  border: 2px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
    filter: brightness(0.9);
  }

  &:hover img {
    transform: scale(1.05);
    filter: brightness(1);
  }

  &::after {
    content: '🎁';
    font-size: 48px;
    opacity: ${props => props.$hasImage ? 0 : 0.4};
    position: absolute;
  }

  /* Loading shimmer effect */
  &.loading {
    background: linear-gradient(
      90deg,
      rgba(0, 215, 255, 0.05) 25%,
      rgba(255, 215, 0, 0.15) 50%,
      rgba(0, 215, 255, 0.05) 75%
    );
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s ease-in-out infinite;
  }
`;

const ProductName = styled.h3`
  font-size: clamp(0.75rem, 1.5vw, 0.95rem);
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.3px;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
  margin-bottom: 4px;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: clamp(0.7rem, 2vw, 0.85rem);
    line-height: 1.2;
  }

  @media (max-width: 480px) {
    font-size: clamp(0.65rem, 2.5vw, 0.8rem);
  }
`;

const ProductDetails = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${theme.spacing.md};
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.9) 40%);
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: ${theme.spacing.sm};
  }

  @media (max-width: 480px) {
    padding: 8px;
  }
`;

const FavoriteButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  color: #fff;
  background: ${props => props.$isWishlisted 
    ? 'linear-gradient(135deg, #4CAF50 0%, #2196F3 100%)' 
    : 'rgba(0, 0, 0, 0.6)'};
  border: 2px solid ${props => props.$isWishlisted ? '#4CAF50' : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  font-weight: 300;
  transition: all 0.3s ease;
  z-index: 10;
  backdrop-filter: blur(10px);
  box-shadow: ${props => props.$isFavorite 
    ? '0 4px 16px rgba(255, 23, 68, 0.5)' 
    : '0 2px 8px rgba(0, 0, 0, 0.3)'};

  &:hover {
    transform: scale(1.15);
    box-shadow: 0 6px 20px rgba(255, 23, 68, 0.6);
    border-color: #FF1744;
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

 
`;

const WishlistButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  color: #fff;
  background: ${props => props.$isWishlisted 
    ? 'linear-gradient(135deg, #4CAF50 0%, #2196F3 100%)' 
    : 'rgba(0, 0, 0, 0.6)'};
  border: 2px solid ${props => props.$isWishlisted ? '#4CAF50' : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  font-weight: 300;
  transition: all 0.3s ease;
  z-index: 10;
  backdrop-filter: blur(10px);
  box-shadow: ${props => props.$isWishlisted 
    ? '0 4px 16px rgba(76, 175, 80, 0.5)' 
    : '0 2px 8px rgba(0, 0, 0, 0.3)'};

  &:hover {
  
    box-shadow: 0 6px 20px rgba(76, 175, 80, 0.6);
    border-color: #4CAF50;
  }

  &:active {
    transform: scale(0.95) 
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1.3rem;
  }
`;

const OutOfStockBadge = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  background: linear-gradient(135deg, #F44336 0%, #D32F2F 100%);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 5;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(244, 67, 54, 0.4);

  @media (max-width: 768px) {
    font-size: 0.65rem;
    padding: 4px 8px;
  }
`;

const ProductPrice = styled.div`
  font-size: clamp(0.85rem, 1.8vw, 1.1rem);
  font-weight: 700;
  color: #ffd700;
  display: flex;
  align-items: center;
  gap: 4px;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
  
  &::before {
    content: '💰';
    font-size: clamp(12px, 2vw, 16px);
  }

  @media (max-width: 768px) {
    font-size: clamp(0.75rem, 2vw, 0.95rem);
  }

  @media (max-width: 480px) {
    font-size: clamp(0.7rem, 2.5vw, 0.85rem);
    gap: 3px;
  }
`;

const DiscountedPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OriginalPrice = styled.span`
  font-size: clamp(0.7rem, 1.4vw, 0.85rem);
  color: rgba(255, 255, 255, 0.5);
  text-decoration: line-through;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: clamp(0.65rem, 1.6vw, 0.75rem);
  }
`;

const DiscountedPrice = styled.div`
  font-size: clamp(0.9rem, 2vw, 1.2rem);
  font-weight: 700;
  color: #00ff88;
  display: flex;
  align-items: center;
  gap: 4px;
  
  @media (max-width: 768px) {
    font-size: clamp(0.8rem, 2.2vw, 1rem);
  }

  @media (max-width: 480px) {
    font-size: clamp(0.75rem, 2.8vw, 0.9rem);
    gap: 3px;
  }
`;

const DiscountBadge = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: #e74c3c;
  color: #ffffff;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    font-size: 0.65rem;
    padding: 3px 6px;
  }
`;


const RegionalFlagsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 6px;
  flex-wrap: nowrap;
  overflow: hidden;

  @media (max-width: 768px) {
    margin-bottom: 4px;
  }
`;

const RegionalFlag = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  overflow: hidden;
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.1);
  margin-left: ${props => props.$index > 0 ? '-6px' : '0'};
  position: relative;
  z-index: ${props => 10 - props.$index};
  transition: all 0.2s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:hover {
    transform: scale(1.15);
    z-index: 20;
    border-color: rgba(255, 215, 0, 0.6);
  }

  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
    margin-left: ${props => props.$index > 0 ? '-5px' : '0'};
  }

  @media (max-width: 480px) {
    width: 16px;
    height: 16px;
    margin-left: ${props => props.$index > 0 ? '-4px' : '0'};
  }
`;

const FlagCounter = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%);
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  color: #ffffff;
  flex-shrink: 0;
  margin-left: -6px;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
    font-size: 8px;
    margin-left: -5px;
  }

  @media (max-width: 480px) {
    width: 16px;
    height: 16px;
    font-size: 7px;
    margin-left: -4px;
  }
`;

const BrandOptionsFooter = styled.div`
  background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.95) 30%);
  padding: ${theme.spacing.sm} ${theme.spacing.md};\n  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: clamp(0.8rem, 1.6vw, 1rem);
  font-weight: 700;
  color: #ffd700;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.8);
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 5;

  &::before {
    content: '🎁';
    font-size: clamp(14px, 2vw, 18px);
  }

  @media (max-width: 768px) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: clamp(0.75rem, 1.8vw, 0.9rem);
  }
`;

const BrandCardContainer = styled.div`
  perspective: 1000px;
  width: 100%;
  position: relative;
`;

const BrandCardInner = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 3/4;
  transition: transform 0.6s;
  transform-style: preserve-3d;
  transform: ${props => props.$flipped ? 'rotateY(180deg)' : 'rotateY(0deg)'};
`;

const BrandCardFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 16px;
  
  @media (max-width: 768px) {
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    border-radius: 10px;
  }
`;

const BrandCardFront = styled(BrandCardFace)`
  background: linear-gradient(180deg, rgba(30, 30, 60, 0.95) 0%, rgba(15, 15, 35, 0.98) 100%);
  border: 3px solid rgba(118, 75, 162, 0.5);
  padding: 0;
  cursor: pointer;
  overflow: hidden;
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  box-shadow: 
    0 8px 24px rgba(0, 0, 0, 0.6),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    border-width: 2px;
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 
      0 16px 40px rgba(102, 126, 234, 0.6),
      0 0 30px rgba(118, 75, 162, 0.4);
    border-color: rgba(102, 126, 234, 0.8);
  }
`;

const BrandCardBack = styled(BrandCardFace)`
  background: linear-gradient(180deg, rgba(30, 30, 60, 0.98) 0%, rgba(15, 15, 35, 1) 100%);
  border: 3px solid ${theme.colors.primary}
  padding: ${theme.spacing.md};
  transform: rotateY(180deg);
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  overflow: hidden;
  backdrop-filter: blur(15px);
  box-shadow: 
    0 8px 24px rgba(102, 126, 234, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
  
  @media (max-width: 768px) {
    border-width: 2px;
    padding: ${theme.spacing.sm};
  }
`;

const FlagsRowContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  flex-wrap: wrap;
  padding: 0 8px;
`;

const SmallFlag = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid ${theme.colors.primary}
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  transition: all 0.3s ease;
  cursor: pointer;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &:hover {
    transform: scale(1.3);
    border-color: rgba(255, 215, 0, 0.8);
    box-shadow: 0 4px 12px ${theme.colors.primary}
    z-index: 10;
  }
`;

const FlipCardFlagsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.sm};
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  align-content: start;
  padding-right: 2px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary};
    border-radius: 3px;
    border: 1px solid rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }
`;

const FlipCardFlag = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  background: transparent;
  height: auto;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  max-width: 100%;
  box-sizing: border-box;
  
  img {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(102, 126, 234, 0.5);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    flex-shrink: 0;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
    
    img {
      border-color: rgba(255, 215, 0, 0.8);
      box-shadow: 0 4px 12px rgba(255, 215, 0, 0.3);
    }
  }
`;

const FlipCardFlagName = styled.span`
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.75rem;
  font-weight: 500;
  text-align: center;
  line-height: 1.1;
  word-break: break-word;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FlipCardTitle = styled.h3`
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.1rem;
  font-weight: 700;
  height: 50px;
  text-align: center;
  margin: 0;
  padding: 0 ${theme.spacing.xl} ${theme.spacing.sm} ${theme.spacing.xl};
  border-bottom: 2px solid rgba(102, 126, 234, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
`;

const CloseFlipButton = styled.button`
  position: absolute;
  top: 10px;
  right: 12px;
  background: rgba(0, 0, 0, 0);
  border: 0px solid rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.8);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.1rem;
  transition: all 0.2s ease;
  z-index: 20;
  padding: 0;
  
  &:hover {
    background: ${theme.colors.error};
    border-color: ${theme.colors.error};
    color: #fff;
    transform: rotate(90deg) scale(1.1);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const Spinner = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid ${theme.colors.inputBorder};
  border-top: 4px solid ${theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.errorBackground};
  border: 2px solid ${theme.colors.errorBorder};
  border-radius: ${theme.borderRadius.medium};
  color: ${theme.colors.error};
  text-align: center;
  margin: ${theme.spacing.xl} 0;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
  animation: ${fadeIn} 0.3s ease-out;
  padding: ${theme.spacing.md};
  box-sizing: border-box;
  overflow-y: auto;

  @media (max-width: 480px) {
    padding: ${theme.spacing.sm};
    align-items: flex-start;
    padding-top: ${theme.spacing.xl};
  }
`;

const ModalContent = styled.div`
  background: ${theme.colors.cardBackgroundThick};
  border: 2px solid;
  border-image: ${theme.colors.cardBorder};
  border-image-slice: 1;
  border-radius: ${theme.borderRadius.large};
  padding: ${theme.spacing.xxl};
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: ${theme.shadows.card};
  animation: ${fadeIn} 0.4s ease-out;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: ${theme.spacing.xl};
    width: 95%;
    max-height: 85vh;
  }

  @media (max-width: 480px) {
    padding: ${theme.spacing.lg};
    width: 100%;
    max-height: 90vh;
    border-radius: ${theme.borderRadius.medium};
  }
`;

const ModalTitle = styled.h2`
  font-size: ${theme.typography.fontSizes.xxl};
  font-weight: ${theme.typography.fontWeights.bold};
  background: ${theme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
`;

const ModalText = styled.p`
  font-size: ${theme.typography.fontSizes.md};
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing.md};
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.xl};
`;

const Button = styled.button`
  flex: 1;
  padding: ${theme.spacing.md};
  background: ${props => props.$primary ? theme.gradients.button : theme.colors.inputBackground};
  color: ${props => props.$primary ? '#000' : theme.colors.textPrimary};
  border: 2px solid ${props => props.$primary ? 'transparent' : theme.colors.inputBorder};
  border-radius: ${theme.borderRadius.medium};
  font-weight: ${theme.typography.fontWeights.semibold};
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.button};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xxl};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSizes.lg};
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  margin: ${theme.spacing.xxl} 0;
  padding: ${theme.spacing.lg};
  background: rgba(30, 30, 60, 0.3);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  animation: ${fadeIn} 0.6s ease-out;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    gap: ${theme.spacing.sm};
    padding: ${theme.spacing.md};
    margin: ${theme.spacing.lg} 0;
  }

  @media (max-width: 480px) {
    gap: ${theme.spacing.xs};
    padding: ${theme.spacing.sm};
    margin: ${theme.spacing.md} 0;
  }
`;

const PaginationButton = styled.button`
  padding: 12px 20px;
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
    : 'rgba(30, 30, 60, 0.5)'};
  color: ${props => props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.7)'};
  border: 2px solid ${props => props.$active 
    ? 'rgba(102, 126, 234, 0.6)' 
    : 'rgba(102, 126, 234, 0.3)'};
  border-radius: 10px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  opacity: ${props => props.disabled ? 0.4 : 1};
  min-width: 48px;
  font-size: 0.9rem;
  box-sizing: border-box;
  box-shadow: ${props => props.$active 
    ? '0 4px 16px rgba(102, 126, 234, 0.4)' 
    : 'none'};

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 0.85rem;
    min-width: 44px;
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 0.75rem;
    min-width: 40px;
    border-radius: 8px;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
    background: ${props => props.$active 
      ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)' 
      : 'rgba(30, 30, 60, 0.7)'};
    border-color: ${theme.colors.primary}
    color: #ffffff;
  }
`;

const PaginationInfo = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  margin: 0 ${theme.spacing.md};
  font-weight: 500;
`;

const ResultsInfo = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
  margin-bottom: ${theme.spacing.lg};
  font-weight: 500;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.textSecondary};
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const GAMING_BRAND_PATTERNS = [
  /xbox/i,
  /playstation|ps\d?/i,
  /steam/i,
  /nintendo/i,
  /epic\s*games?/i,
  /battle\.net|blizzard/i,
  /riot\s*games?/i,
  /discord/i,
  /twitch/i,
  /ea\b|origin/i,
  /ubisoft/i,
  /rockstar/i,
  /gog/i,
  /razor|razer/i,
  /humble/i,
  /pubg|playerunknown/i,
  /roblox/i,
  /minecraft/i,
  /fortnite/i,
  /call of duty|\bcod\b/i,
  /free\s*fire/i,
  /mobile\s*legends|mlbb/i,
  /genshin/i,
  /league of legends|\blol\b/i,
  /valorant/i,
  /apex/i,
  /overwatch/i,
  /counter-?strike|cs:?go|cs2/i,
  /dota\s*2/i,
  /grand theft auto|\bgta\b/i,
  /world of warcraft|\bwow\b/i,
  /final fantasy|\bff\s*\d+/i,
  /pokemon|pokémon/i,
  /candy\s*crush/i,
  /clash of clans|clash royale/i,
  /among us/i,
  /fall\s*guys/i,
  /rocket\s*league/i,
  /rainbow\s*six/i,
  /destiny\s*\d?/i,
  /the\s*sims/i,
  /red\s*dead/i,
  /fifa/i,
  /nba\s*2k/i,
  /madden/i,
  /google\s*play/i
];

const GAMING_KEYWORDS = [
  'tamashi',
  'path',
  'super',
  'punishing',
  'honkai',
  'honkai impact',
  'impact 3rd',
  'game',
  'metal',
  'slug',
  'slugfest',
  'pixel',
  'arena',
  'heroes',
  'magic',
  'honor',
  'clash',
  'clash of clans',
  'among us',
  'fall guys',
  'rocket league',
  'rainbow six',
  'destiny',
  'the sims',
  'red dead',
  'fifa',
  'nba 2k',
  'madden',
  'google play',
  'yalla',
  'undawn',
  'state',
  'tarbi',
  'jawaker',
  'tom and jerry',
  'identity',
  'speed drifters',
  'marvel rivals',
  'the lord of the rings',
  'game',
  'gaming',
  'xbox',
  'playstation',
  'psn',
  'ps plus',
  'psplus',
  'ps store',
  'playstation plus',
  'nintendo',
  'switch',
  'eshop',
  'steam',
  'steam wallet',
  'battle.net',
  'blizzard',
  'riot',
  'valorant',
  'league of legends',
  'wild rift',
  'tft',
  'roblox',
  'minecraft',
  'fortnite',
  'v-bucks',
  'call of duty',
  'cod',
  'codm',
  'pubg',
  'pubg mobile',
  'free fire',
  'garena',
  'mobile legends',
  'mlbb',
  'genshin',
  'honkai',
  'overwatch',
  'dota',
  'counter-strike',
  'csgo',
  'cs2',
  'fifa',
  'ea fc',
  'apex',
  'rainbow six',
  'r6',
  'final fantasy',
  'wow',
  'world of warcraft',
  'runescape',
  'osrs',
  'gta',
  'grand theft auto',
  'rockstar',
  'ubisoft',
  'epic',
  'origin',
  'ea',
  'xbox game pass',
  'game pass',
  'google play'
];

const normalizeRegion = (value) => (value || '').toString().trim().toLowerCase();
const isGlobalRegionName = (name) => {
  const normalized = normalizeRegion(name);
  return ['global', 'international', 'worldwide', 'all regions', 'all'].some((token) => normalized.includes(token));
};
const getCountryNameFromCode = (code) => {
  if (!code) return '';
  if (typeof code === 'string' && code.length > 2) return code;
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return displayNames.of(code) || '';
  } catch (err) {
    return '';
  }
};
const getUserCountryTokens = (userData) => {
  const rawCountry = userData?.metadata?.country || userData?.country || userData?.countryCode || userData?.region || '';
  const countryName = getCountryNameFromCode(rawCountry);
  return [countryName, rawCountry]
    .filter(Boolean)
    .map((value) => normalizeRegion(value));
};
const isPreferredRegionMatch = (product, preferredTokens) => {
  if (!preferredTokens.length) return false;
  const regionName = normalizeRegion(product?.regionName || product?.region || product?.country || '');
  return preferredTokens.some((token) => regionName.includes(token));
};

// Main Component
const Catalog = ({ onNavigate }) => {
  // Include updateUserPoints so we can push real-time updates to global auth context
  const { user, updateUserPoints } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userPoints, setUserPoints] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('gaming');
  const [sortBy, setSortBy] = useState('name');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [, setTotalProducts] = useState(0);
  const [itemsPerPage] = useState(20);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  const [redemptionResult, setRedemptionResult] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [imageLoadStates, setImageLoadStates] = useState({});
  const [viewMode, setViewMode] = useState('brands'); // 'brands' or 'products'
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandGroups, setBrandGroups] = useState([]);
  const [favoriteBrands, setFavoriteBrands] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loadingWishlist, setLoadingWishlist] = useState(false);
  const [, setSelectedCountry] = useState(null);
  const [flippedBrands, setFlippedBrands] = useState(new Set());
  const preferredRegionTokens = useMemo(() => getUserCountryTokens(user), [user]);
  
  // Session-based fetch guard to prevent excessive API bursts
  const hasInitialFetchRef = useRef(false);
  const userIdRef = useRef(null);

  // Extract brand name from product name
  const extractBrand = (productName) => {
    // Common patterns for brands and popular games
    const brandPatterns = [
      // Gaming brands & stores
      /^(Apple)/i,
      /^(Google Play|Google)/i,
      /^(Amazon)/i,
      /^(Xbox)/i,
      /^(PlayStation|PS\d?)/i,
      /^(Steam)/i,
      /^(Netflix)/i,
      /^(Spotify)/i,
      /^(Samsung)/i,
      /^(Nintendo)/i,
      /^(Visa)/i,
      /^(Mastercard)/i,
      /^(Epic Games)/i,
      /^(Battle\.net|Blizzard)/i,
      /^(Riot Games)/i,
      /^(Discord)/i,
      /^(Twitch)/i,
      /^(EA|Origin)/i,
      /^(Ubisoft)/i,
      /^(Rockstar)/i,
      /^(GOG)/i,
      /^(Disney)/i,
      /^(Hulu)/i,
      /^(Crunchyroll)/i,
      /^(YouTube)/i,
      /^(Razer)/i,
      /^(Humble)/i,
      /^(Walmart)/i,
      /^(Target)/i,
      /^(Best Buy)/i,
      /^(eBay)/i,
      /^(Uber)/i,
      /^(DoorDash)/i,
      /^(Starbucks)/i,
      /^(Chipotle)/i,
      
      // Popular games that should be grouped
      /(PUBG|PlayerUnknown'?s?\s*Battlegrounds?)/i,
      /(Roblox)/i,
      /(Minecraft)/i,
      /(Fortnite)/i,
      /(Call of Duty|COD)/i,
      /(Free Fire)/i,
      /(Mobile Legends|MLBB)/i,
      /(Genshin Impact)/i,
      /(League of Legends|LoL)/i,
      /(Valorant)/i,
      /(Apex Legends)/i,
      /(Overwatch)/i,
      /(Counter-Strike|CS:?GO|CS2)/i,
      /(Dota\s*2)/i,
      /(Grand Theft Auto|GTA)/i,
      /(World of Warcraft|WoW)/i,
      /(Final Fantasy|FF\s*\d+)/i,
      /(Pokemon|Pokémon)/i,
      /(Candy Crush)/i,
      /(Clash of Clans|Clash Royale)/i,
      /(Among Us)/i,
      /(Fall Guys)/i,
      /(Rocket League)/i,
      /(Rainbow Six)/i,
      /(Destiny\s*\d?)/i,
      /(The Sims)/i,
      /(Red Dead)/i,
      /(FIFA)/i,
      /(NBA\s*2K)/i,
      /(Madden)/i,
      
      /^([A-Z][a-z]+)/  // Fallback: first capitalized word
    ];

    for (const pattern of brandPatterns) {
      const match = productName.match(pattern);
      if (match) {
        // Return the captured group, clean up common variations
        let brand = match[1];
        
        // Normalize brand names
        if (/pubg|playerunknown/i.test(brand)) return 'PUBG';
        if (/call of duty|cod/i.test(brand)) return 'Call of Duty';
        if (/mobile legends|mlbb/i.test(brand)) return 'Mobile Legends';
        if (/league of legends|lol/i.test(brand)) return 'League of Legends';
        if (/counter-strike|csgo|cs2/i.test(brand)) return 'Counter-Strike';
        if (/grand theft auto|gta/i.test(brand)) return 'Grand Theft Auto';
        if (/world of warcraft|wow/i.test(brand)) return 'World of Warcraft';
        if (/pokemon|pokémon/i.test(brand)) return 'Pokémon';
        if (/clash of clans/i.test(brand)) return 'Clash of Clans';
        if (/clash royale/i.test(brand)) return 'Clash Royale';
        if (/rainbow six/i.test(brand)) return 'Rainbow Six';
        if (/red dead/i.test(brand)) return 'Red Dead';
        if (/battle\.net|blizzard/i.test(brand)) return 'Blizzard';
        
        return brand;
      }
    }
    return 'Other';
  };

  const getFavoriteBrandName = (favorite) => favorite?.attributes?.brandName || favorite?.brandName || '';

  const isGamingBrandName = (brandName = '') => (
    GAMING_BRAND_PATTERNS.some((pattern) => pattern.test(brandName))
  );

  const isGamingProduct = (product) => {
    const name = (product?.displayName || product?.productName || product?.merchantName || '').toLowerCase();
    const category = (product?.category || '').toLowerCase();
    if (category.includes('gaming') || category.includes('games') || category.includes('game')) {
      return true;
    }
    if (GAMING_BRAND_PATTERNS.some((pattern) => pattern.test(name))) {
      return true;
    }
    return GAMING_KEYWORDS.some((keyword) => name.includes(keyword) || category.includes(keyword));
  };

  const isGamingGroup = useCallback((group) => {
    if (isGamingBrandName(group.brand)) return true;
    return Array.isArray(group.products) && group.products.some((product) => isGamingProduct(product));
  }, []);

  const getPreferredRegionTokensFromSession = () => {
    const raw = sessionStorage.getItem('preferredRegionTokens');
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      return [parsed];
    } catch (err) {
      return [raw];
    }
  };
  const filterBrandProductsByRegion = useCallback((brand, tokens, targetProductId = null) => {
    if (!tokens.length) {
      return { products: brand.products, selectedRegion: null };
    }
    const normalizedTokens = tokens.map(normalizeRegion).filter(Boolean);
    const regionMatches = brand.products.filter((product) => {
      const regionName = normalizeRegion(product.regionName || product.region || product.country || '');
      return normalizedTokens.some((token) => regionName.includes(token));
    });
    const globalMatches = brand.products.filter((product) => isGlobalRegionName(product.regionName || product.region || product.country));
    const pickList = regionMatches.length > 0 ? regionMatches : globalMatches.length > 0 ? globalMatches : brand.products;

    if (targetProductId && !pickList.some((product) => product.id === targetProductId)) {
      return { products: brand.products, selectedRegion: null };
    }

    const selectedRegion = pickList[0]?.regionName || pickList[0]?.region || pickList[0]?.country || null;
    return { products: pickList, selectedRegion };
  }, []);

  const filterProductsByPreferredRegion = useCallback((productsList) => {
    if (!Array.isArray(productsList) || productsList.length === 0) {
      return [];
    }

    if (!preferredRegionTokens.length) {
      return productsList;
    }

    const preferredMatches = productsList.filter((product) => isPreferredRegionMatch(product, preferredRegionTokens));
    if (preferredMatches.length > 0) {
      return preferredMatches;
    }

    const globalMatches = productsList.filter((product) => isGlobalRegionName(product?.regionName || product?.region || product?.country));
    if (globalMatches.length > 0) {
      return globalMatches;
    }

    const noRegionMatches = productsList.filter((product) => !normalizeRegion(product?.regionName || product?.region || product?.country));
    return noRegionMatches.length > 0 ? noRegionMatches : productsList;
  }, [preferredRegionTokens]);

  // Group products by brand
  const groupProductsByBrand = useCallback((productsArray) => {
    const groups = {};
    productsArray.forEach(product => {
      const brand = extractBrand(product.merchantName || product.displayName || product.productName || product.title || 'Unknown');
      if (!groups[brand]) {
        groups[brand] = {
          brand,
          products: [],
          image: product.image,
          count: 0,
          regions: new Set(),
          regionImages: new Map(),
          hasDiscount: false,
          maxDiscount: 0
        };
      }
      groups[brand].products.push(product);
      groups[brand].count++;
      
      // Check for discount and aggregate
      if (product.hasDiscount) {
        groups[brand].hasDiscount = true;
        if (product.discountPercent > groups[brand].maxDiscount) {
            groups[brand].maxDiscount = product.discountPercent;
        }
      }
      
      // Extract region from product (regionImage is a string, not array)
      if (product.regionImage && product.regionName) {
        groups[brand].regions.add(product.regionName);
        groups[brand].regionImages.set(product.regionName, product.regionImage);
      }
    });
    
    // Convert Sets to Arrays for easier rendering
    const groupsArray = Object.values(groups).map(group => ({
      ...group,
      regions: Array.from(group.regions),
      regionImages: Array.from(group.regionImages.entries()).map(([name, url]) => ({ name, url }))
    }));

    // Sort: "Other" last, rest by count (descending)
    groupsArray.sort((a, b) => {
      if (a.brand === 'Other') return 1;
      if (b.brand === 'Other') return -1;
      return b.count - a.count;
    });
    
    return groupsArray;
  }, []);

  const favoriteBrandSet = useMemo(() => {
    return new Set(favoriteBrands.map(getFavoriteBrandName).filter(Boolean));
  }, [favoriteBrands]);

  const localizedFavoriteBrandGroups = useMemo(() => {
    return brandGroups
      .filter((group) => favoriteBrandSet.has(group.brand))
      .map((group) => {
        const filteredProducts = filterProductsByPreferredRegion(group.products);
        return {
          ...group,
          products: filteredProducts,
          count: filteredProducts.length
        };
      })
      .filter((group) => group.count > 0);
  }, [brandGroups, favoriteBrandSet, filterProductsByPreferredRegion]);

  const wishlistProducts = useMemo(() => {
    if (!wishlistItems.length) return [];
    const productsById = new Map(products.map((product) => [product.id, product]));

    const baseList = wishlistItems.map((item) => {
      const productRef = item?.attributes?.productRef || item?.productRef;
      const matchingProduct = productRef ? productsById.get(productRef) : null;
      if (matchingProduct) {
        return matchingProduct;
      }

      return {
        id: productRef,
        productName: item?.attributes?.productName || item?.productName || 'Wishlisted Gift Card',
        displayName: item?.attributes?.productName || item?.productName || 'Wishlisted Gift Card',
        image: item?.attributes?.productImage || item?.productImage,
        sellingPrice: item?.attributes?.productPrice || item?.productPrice,
        available: 0,
        brandName: item?.attributes?.brandName || item?.brandName || ''
      };
    }).filter(Boolean);

    const localizedList = filterProductsByPreferredRegion(baseList);

    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return localizedList;
    }

    return localizedList.filter((product) => {
      const name = (product.displayName || product.productName || '').toLowerCase();
      const brand = (product.brandName || '').toLowerCase();
      return name.includes(normalizedSearch) || brand.includes(normalizedSearch);
    });
  }, [wishlistItems, products, searchTerm, filterProductsByPreferredRegion]);

  const gamingProductCount = useMemo(() => {
    return brandGroups.reduce((sum, group) => sum + (isGamingGroup(group) ? group.count : 0), 0);
  }, [brandGroups, isGamingGroup]);

  const othersProductCount = useMemo(() => {
    return brandGroups.reduce((sum, group) => sum + (!isGamingGroup(group) ? group.count : 0), 0);
  }, [brandGroups, isGamingGroup]);

  const favoritesProductCount = useMemo(() => {
    return localizedFavoriteBrandGroups.reduce((sum, group) => sum + group.count, 0);
  }, [localizedFavoriteBrandGroups]);

  const wishlistProductCount = useMemo(() => wishlistProducts.length, [wishlistProducts]);

  const discountedProductCount = useMemo(() => {
    return brandGroups.reduce((sum, group) => sum + (group.hasDiscount ? group.count : 0), 0);
  }, [brandGroups]);

  const displayedBrandGroups = useMemo(() => {
    if (selectedCategory === 'favorites') {
      return localizedFavoriteBrandGroups;
    }
    if (selectedCategory === 'gaming') {
      return brandGroups.filter((group) => isGamingGroup(group));
    }
    if (selectedCategory === 'discounted') {
      return brandGroups.filter((group) => group.hasDiscount);
    }
    if (selectedCategory === 'others') {
      return brandGroups.filter((group) => !isGamingGroup(group));
    }
    return brandGroups;
  }, [brandGroups, localizedFavoriteBrandGroups, selectedCategory, isGamingGroup]);

  const displayedBrandProductCount = useMemo(() => {
    return displayedBrandGroups.reduce((sum, group) => sum + group.count, 0);
  }, [displayedBrandGroups]);

  const sortedSelectedBrandProducts = useMemo(() => {
    if (!selectedBrand?.products) return [];
    return [...selectedBrand.products].sort((a, b) => {
      const aAvailable = (a.available || 0) > 0 ? 1 : 0;
      const bAvailable = (b.available || 0) > 0 ? 1 : 0;
      if (aAvailable !== bAvailable) return bAvailable - aAvailable;
      return 0;
    });
  }, [selectedBrand]);

  const filterOptions = useMemo(() => (
    [
      { key: 'gaming', label: 'Gaming', count: gamingProductCount, icon: <FaGamepad /> },
      { key: 'discounted', label: 'On Sale', count: discountedProductCount, icon: '🏷️' },
      { key: 'others', label: 'Others', count: othersProductCount, icon: <FaGift /> },
      { key: 'favorites', label: 'Favorites', count: favoritesProductCount, icon: '❤️' },
      { key: 'wishlist', label: 'Wishlist', count: wishlistProductCount, icon: '💝' }
    ]
  ), [gamingProductCount, discountedProductCount, othersProductCount, favoritesProductCount, wishlistProductCount]);

  // Sync user points from auth context
  const fetchUserPoints = useCallback(() => {
    if (user?.points != null) {
      setUserPoints(user.points);
    }
  }, [user]);

  // Fetch favorite brands
  const fetchFavoriteBrands = useCallback(async () => {
    try {
      if (!user) return;

      const favorites = await ApiService.getFavoriteBrands(user.id);
      setFavoriteBrands(favorites);
    } catch (err) {
      console.error('Error fetching favorite brands:', err);
    }
  }, [user]);

  // Toggle favorite brand
  const toggleFavorite = async (brand) => {
    if (loadingFavorites) return;

    try {
      setLoadingFavorites(true);
      
      // Check if already favorited
      const existingFavorite = favoriteBrands.find(
        fav => fav.attributes?.brandName === brand.brand || fav.brandName === brand.brand
      );

      if (existingFavorite) {
        // Remove favorite
        const favoriteId = existingFavorite.id;
        await ApiService.removeFavoriteBrand(favoriteId);
      } else {
        // Check if user already has 3 favorites
        if (favoriteBrands.length >= 3) {
          alert('You can only have 3 favorite brands. Please remove one first.');
          setLoadingFavorites(false);
          return;
        }
        // Add favorite
        await ApiService.addFavoriteBrand(user.id, brand.brand, brand.image);
      }

      // Refresh favorites list
      await fetchFavoriteBrands();
    } catch (err) {
      console.error('Error toggling favorite:', err);
      alert('Failed to update favorite brand');
    } finally {
      setLoadingFavorites(false);
    }
  };

  // Check if brand is favorited
  const isFavorite = (brandName) => {
    return favoriteBrands.some(
      fav => fav.attributes?.brandName === brandName || fav.brandName === brandName
    );
  };

  // Fetch wishlist items
  const fetchWishlist = useCallback(async () => {
    try {
      if (!user) return;

      const wishlist = await ApiService.getWishlist(user.id);
      setWishlistItems(wishlist);
    } catch (err) {
      console.error('Error fetching wishlist:', err);
    }
  }, [user]);

  // Toggle wishlist for unavailable products
  const toggleWishlist = async (product) => {
    if (loadingWishlist) return;

    try {
      setLoadingWishlist(true);
      
      // Check if already in wishlist
      const existingItem = wishlistItems.find(
        item => (item.attributes?.productRef || item.productRef) === product.id
      );

      if (existingItem) {
        // Remove from wishlist
        const itemId = existingItem.id;
        await ApiService.removeFromWishlist(itemId);
      } else {
        // Add to wishlist
        await ApiService.addToWishlist(user.id, product);
      }

      // Refresh wishlist
      await fetchWishlist();
    } catch (err) {
      console.error('Error toggling wishlist:', err);
      alert('Failed to update wishlist');
    } finally {
      setLoadingWishlist(false);
    }
  };

  // Check if product is in wishlist
  const isInWishlist = (productId) => {
    return wishlistItems.some(
      item => (item.attributes?.productRef || item.productRef) === productId
    );
  };

  // Check if product is available (always show all products)
  const isProductAvailable = (product) => {
    return true; // Show all products, handle stock display in UI
  };

  // Check if product is in stock
  const isProductInStock = (product) => {
    return product.available > 0;
  };

  // Static unavailable gaming & entertainment brands for wishlist
  // Note: unavailableBrands array removed - now handled server-side in backend product controller

  // Fetch products with pagination
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // When in brands view, fetch all products to show all brands
      // When in products view, use pagination for the selected brand
      const fetchLimit = viewMode === 'brands' ? 10000 : itemsPerPage;
      
      // Fetch products from backend - all processing happens server-side
      // - Pricing calculated (faceValue × 1000)
      // - Test products filtered
      // - Static brands included
      // - Brand extraction done
      const response = await ProductService.getCatalog({
        page: currentPage,
        limit: fetchLimit,
        search: searchTerm,
        category: ['gaming', 'others', 'favorites', 'wishlist', 'discounted'].includes(selectedCategory) ? '' : selectedCategory,
        sortBy: sortBy
      });

      console.log('[Catalog.fetchProducts] Response from ProductService:', {
        success: response?.success,
        hasData: !!response?.data,
        hasProducts: !!response?.data?.products,
        productCount: response?.data?.products?.length,
        structure: response
      });

      if (response?.success && response.data?.products) {
        // Products are already processed server-side
        let allProducts = response.data.products;

        if (viewMode === 'brands') {
          const totalPagesToFetch = response.data.pagination?.totalPages || 1;
          if (totalPagesToFetch > 1) {
            const pageRequests = [];
            for (let page = 2; page <= totalPagesToFetch; page += 1) {
              pageRequests.push(ProductService.getCatalog({
                page,
                limit: fetchLimit,
                search: searchTerm,
                category: ['gaming', 'others', 'favorites', 'wishlist', 'discounted'].includes(selectedCategory) ? '' : selectedCategory,
                sortBy: sortBy
              }));
            }

            const pageResponses = await Promise.all(pageRequests);
            pageResponses.forEach((pageResponse) => {
              if (pageResponse?.success && pageResponse.data?.products) {
                allProducts = allProducts.concat(pageResponse.data.products);
              }
            });
          }
        }
        
        console.log('[Catalog.fetchProducts] Processing products:', {
          count: allProducts.length,
          categories: response.data.categories?.length || 0,
          pagination: response.data.pagination
        });
        
        setProducts(allProducts);
        setBrandGroups(groupProductsByBrand(allProducts));
        setTotalPages(response.data.pagination?.totalPages || 1);
        setTotalProducts(response.data.pagination?.total || allProducts.length);
        
        // Initialize image load states
        const loadStates = {};
        allProducts.forEach(p => {
          if (p.image) {
            loadStates[p.id] = 'loading';
          }
        });
        setImageLoadStates(loadStates);
      } else {
        console.error('[Catalog.fetchProducts] Invalid response structure:', {
          response,
          success: response?.success,
          hasData: !!response?.data,
          hasProducts: !!response?.data?.products
        });
        throw new Error('Failed to fetch products - invalid response structure');
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.message || 'Failed to load gift card catalog');
    } finally {
      setLoading(false);
    }
  }, [viewMode, itemsPerPage, currentPage, searchTerm, selectedCategory, sortBy, groupProductsByBrand]);

  // Handle image load success
  const handleImageLoad = (productId) => {
    setImageLoadStates(prev => ({ ...prev, [productId]: 'loaded' }));
  };

  // Handle image load error
  const handleImageError = (productId) => {
    setImageLoadStates(prev => ({ ...prev, [productId]: 'error' }));
  };

  // Helper function to get and parse user metadata
  const getUserMetadata = () => {
    try {
      if (user?.clickedStoreItems) {
        return typeof user.clickedStoreItems === 'string'
          ? JSON.parse(user.clickedStoreItems)
          : user.clickedStoreItems;
      }
    } catch (e) {
      console.error('Error parsing clickedStoreItems:', e);
    }
    return {};
  };

  // Helper function to update user metadata
  const updateUserMetadata = useCallback(async (clickedStoreItems) => {
    try {
      const response = await ApiService.updateUser(user.id, {
        clickedStoreItems: clickedStoreItems
      });
      return response.success;
    } catch (error) {
      console.error('Error updating user metadata:', error);
      return false;
    }
  }, [user]);

  // Track brand click (when flipping brand card)
  const trackBrandClick = useCallback(async (brandName) => {
    if (!user || !brandName) return;

    try {
      const clickedStoreItems = await getUserMetadata();

      // Initialize brand if it doesn't exist
      if (!clickedStoreItems[brandName]) {
        clickedStoreItems[brandName] = {
          clicks: 1
        };
      } else {
        clickedStoreItems[brandName].clicks = (clickedStoreItems[brandName].clicks || 0) + 1;
      }

      const success = await updateUserMetadata(clickedStoreItems);
      if (success) {
        console.log('✅ Brand click tracked:', {
          brand: brandName,
          clicks: clickedStoreItems[brandName].clicks
        });
      }
    } catch (err) {
      console.error('Error tracking brand click:', err);
    }
  }, [user, updateUserMetadata]);

  // Handle brand selection
  const handleBrandClick = useCallback((brand, event) => {
    // Prevent flip when clicking flags or footer
    if (event?.target?.closest?.('.small-flag') || event?.target?.closest?.('.brand-footer')) {
      return;
    }
    
    // Track brand click
    trackBrandClick(brand.brand);
    
    if (brand.regionImages && brand.regionImages.length > 0) {
      // Toggle flip for multi-region brands
      setFlippedBrands(prev => {
        const newSet = new Set(prev);
        if (newSet.has(brand.brand)) {
          newSet.delete(brand.brand);
        } else {
          newSet.add(brand.brand);
        }
        return newSet;
      });
    } else {
      // No regions, go directly to products
      setSelectedBrand(brand);
      setViewMode('products');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [trackBrandClick]);

  // Handle country selection
  const handleCountrySelect = (country, brand) => {
    // Track country click
    trackCountryClick(brand.brand, country.name);
    
    setSelectedCountry(country);
    
    // Filter products by region (regionName is a string, not an array)
    const filteredProducts = brand.products.filter(product => 
      product.regionName === country.name
    );
    
    setSelectedBrand({
      ...brand,
      products: filteredProducts.length > 0 ? filteredProducts : brand.products,
      count: filteredProducts.length > 0 ? filteredProducts.length : brand.count,
      selectedRegion: country.name
    });
    setViewMode('products');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle back to brands
  const handleBackToBrands = () => {
    setViewMode('brands');
    setSelectedBrand(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Track country click (when clicking flag)
  const trackCountryClick = async (brandName, countryName) => {
    if (!user || !brandName || !countryName) return;

    try {
      const clickedStoreItems = await getUserMetadata();

      // Initialize brand if it doesn't exist
      if (!clickedStoreItems[brandName]) {
        clickedStoreItems[brandName] = {
          clicks: 0
        };
      }

      // Initialize country if it doesn't exist
      if (!clickedStoreItems[brandName][countryName]) {
        clickedStoreItems[brandName][countryName] = {
          clicks: 1
        };
      } else {
        clickedStoreItems[brandName][countryName].clicks = (clickedStoreItems[brandName][countryName].clicks || 0) + 1;
      }

      // Recalculate brand total clicks
      let brandTotalClicks = 0;
      Object.keys(clickedStoreItems[brandName]).forEach(key => {
        if (key !== 'clicks') {
          brandTotalClicks += (clickedStoreItems[brandName][key].clicks || 0);
        }
      });
      clickedStoreItems[brandName].clicks = brandTotalClicks;

      const success = await updateUserMetadata(clickedStoreItems);
      if (success) {
        console.log('✅ Country click tracked:', {
          brand: brandName,
          country: countryName,
          countryClicks: clickedStoreItems[brandName][countryName].clicks,
          brandClicks: clickedStoreItems[brandName].clicks
        });
      }
    } catch (err) {
      console.error('Error tracking country click:', err);
    }
  };

  // Track item click (when clicking product)
  const trackItemClick = async (product) => {
    if (!user || !product) return;

    try {
      const brandName = extractBrand(product.merchantName || product.displayName || product.productName || product.name);
      const countryName = product.regionName || 'Unknown';
      const itemId = product.id;
      const itemName = product.displayName || product.productName || product.name;

      const clickedStoreItems = await getUserMetadata();

      // Initialize brand if it doesn't exist
      if (!clickedStoreItems[brandName]) {
        clickedStoreItems[brandName] = {
          clicks: 0
        };
      }

      // Initialize country if it doesn't exist
      if (!clickedStoreItems[brandName][countryName]) {
        clickedStoreItems[brandName][countryName] = {
          clicks: 0
        };
      }

      // Update or create item entry
      if (clickedStoreItems[brandName][countryName][itemId]) {
        clickedStoreItems[brandName][countryName][itemId].clicks += 1;
      } else {
        clickedStoreItems[brandName][countryName][itemId] = {
          name: itemName,
          clicks: 1
        };
      }

      // Recalculate country-level clicks
      let countryTotalClicks = 0;
      Object.keys(clickedStoreItems[brandName][countryName]).forEach(key => {
        if (key !== 'clicks') {
          countryTotalClicks += (clickedStoreItems[brandName][countryName][key].clicks || 0);
        }
      });
      clickedStoreItems[brandName][countryName].clicks = countryTotalClicks;

      // Recalculate brand-level clicks
      let brandTotalClicks = 0;
      Object.keys(clickedStoreItems[brandName]).forEach(key => {
        if (key !== 'clicks') {
          brandTotalClicks += (clickedStoreItems[brandName][key].clicks || 0);
        }
      });
      clickedStoreItems[brandName].clicks = brandTotalClicks;

      const success = await updateUserMetadata(clickedStoreItems);
      if (success) {
        console.log('✅ Item click tracked:', {
          brand: brandName,
          brandClicks: clickedStoreItems[brandName].clicks,
          country: countryName,
          countryClicks: clickedStoreItems[brandName][countryName].clicks,
          item: itemName,
          itemClicks: clickedStoreItems[brandName][countryName][itemId].clicks
        });
      }
    } catch (err) {
      console.error('Error tracking item click:', err);
    }
  };

  // Handle redemption
  const handleRedeem = async () => {
    if (!selectedProduct || !user) return;

    try {
      setRedeeming(true);
      
      // Validate product server-side before redemption
      const validation = await ProductService.validateProduct(
        selectedProduct.id,
        user.id
      );
      
      if (!validation.isValid) {
        // Show validation errors
        const errorMessage = validation.errors?.join(', ') || 'Product validation failed';
        alert(errorMessage);
        setRedeeming(false);
        return;
      }

      // Redeem through Strapi backend (which proxies to Wupex)
      const result = await ProductService.redeemGiftCard(
        selectedProduct.id,
        1
      );

      if (result.success) {
        // Extract new points value from response
        const newPoints = result.newTotalPoints ?? result.data?.remainingPoints ?? result.data?.newTotalPoints;
        
        console.log('[Catalog.handleRedeem] Redemption success! New points:', newPoints);
        console.log('[Catalog.handleRedeem] Full result:', result);
        
        // Immediate global points update using unified schema (BEFORE modal)
        if (typeof newPoints === 'number') {
          console.log('[Catalog.handleRedeem] ✅ Updating navbar points to:', newPoints);
          
          // Update global auth context (navbar will re-render)
          if (updateUserPoints) {
            updateUserPoints(newPoints);
            console.log('[Catalog.handleRedeem] ✅ updateUserPoints called');
          } else {
            console.error('[Catalog.handleRedeem] ❌ updateUserPoints is not available!');
          }
          
          // Update local state
          setUserPoints(newPoints);
          console.log('[Catalog.handleRedeem] ✅ Local points state updated');
        } else {
          console.error('[Catalog.handleRedeem] ❌ No valid points value found in response');
        }
        
        // Fetch fresh points from server to confirm
        console.log('[Catalog.handleRedeem] Fetching latest points from server...');
        await fetchUserPoints();
        
        // Show success modal after points update
        setRedemptionResult({ ...result.data, newTotalPoints: newPoints });
        setShowRedemptionModal(true);
      } else {
        throw new Error(result.message || 'Redemption failed');
      }
    } catch (err) {
      console.error('Error redeeming gift card:', err);
      alert(err.message || 'Failed to redeem gift card');
    } finally {
      setRedeeming(false);
      setSelectedProduct(null);
    }
  };

  // Reset to page 1 and brand view only when explicit filters change
  useEffect(() => {
    setCurrentPage(1);
    if (selectedCategory === 'wishlist') {
      setViewMode('products');
      setSelectedBrand((prev) => {
        if (prev?.brand === 'Wishlist') {
          return prev;
        }

        return {
          brand: 'Wishlist',
          products: [],
          count: 0
        };
      });
    } else {
      setViewMode('brands');
      setSelectedBrand(null);
    }
  }, [searchTerm, selectedCategory, sortBy]);

  useEffect(() => {
    if (selectedCategory === 'wishlist') {
      setSelectedBrand({
        brand: 'Wishlist',
        products: wishlistProducts,
        count: wishlistProducts.length
      });
    }
  }, [selectedCategory, wishlistProducts]);

  // Fetch favorites and wishlist on mount
  useEffect(() => {
    if (user) {
      fetchFavoriteBrands();
      fetchWishlist();
    }
  }, [user, fetchFavoriteBrands, fetchWishlist]);

  // Handle navigation from Dashboard (brand filter, view product, or redeem product)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user || products.length === 0) return;

    const preferredRegionTokens = getPreferredRegionTokensFromSession();
    if (preferredRegionTokens.length > 0) {
      sessionStorage.removeItem('preferredRegionTokens');
    }

    // Check for brand filter from Dashboard
    const filterByBrand = sessionStorage.getItem('filterByBrand');
    if (filterByBrand) {
      sessionStorage.removeItem('filterByBrand');
      
      // Find the brand group and select it
      const brand = brandGroups.find(group => 
        group.brand.toLowerCase() === filterByBrand.toLowerCase()
      );
      
      if (brand) {
        if (preferredRegionTokens.length > 0) {
          const { products: filteredProducts, selectedRegion } = filterBrandProductsByRegion(brand, preferredRegionTokens);
          setSelectedBrand({
            ...brand,
            products: filteredProducts,
            count: filteredProducts.length,
            selectedRegion: selectedRegion || undefined
          });
          setSelectedCountry(selectedRegion ? { name: selectedRegion } : null);
          setViewMode('products');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          handleBrandClick(brand);
        }
      }
    }

    // Check for product to view from Dashboard
    const viewProduct = sessionStorage.getItem('viewProduct');
    if (viewProduct) {
      sessionStorage.removeItem('viewProduct');
      try {
        const product = JSON.parse(viewProduct);
        // Find the brand that contains this product
        const brand = brandGroups.find(group =>
          group.products.some(p => p.id === product.id)
        );
        
        if (brand) {
          if (preferredRegionTokens.length > 0) {
            const { products: filteredProducts, selectedRegion } = filterBrandProductsByRegion(brand, preferredRegionTokens, product.id);
            setSelectedBrand({
              ...brand,
              products: filteredProducts,
              count: filteredProducts.length,
              selectedRegion: selectedRegion || undefined
            });
            setSelectedCountry(selectedRegion ? { name: selectedRegion } : null);
            setViewMode('products');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            handleBrandClick(brand);
          }
          // Scroll to the product after a short delay
          setTimeout(() => {
            const productCard = document.querySelector(`[data-product-id="${product.id}"]`);
            if (productCard) {
              productCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
              productCard.style.boxShadow = '0 0 20px rgba(102, 126, 234, 0.8)';
              setTimeout(() => {
                productCard.style.boxShadow = '';
              }, 2000);
            }
          }, 500);
        }
      } catch (err) {
        console.error('Error parsing viewProduct:', err);
      }
    }

    // Check for product to redeem from Dashboard
    const selectedProductData = sessionStorage.getItem('selectedProduct');
    if (selectedProductData) {
      sessionStorage.removeItem('selectedProduct');
      try {
        const product = JSON.parse(selectedProductData);
        // Find and select the product directly
        const foundProduct = products.find(p => p.id === product.id);
        if (foundProduct && isProductAvailable(foundProduct)) {
          setSelectedProduct(foundProduct);
        }
      } catch (err) {
        console.error('Error parsing selectedProduct:', err);
      }
    }
  }, [user, products, brandGroups, filterBrandProductsByRegion, handleBrandClick]);

  // Initial mount: fetch products once per session when user is available
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user && !hasInitialFetchRef.current) {
      hasInitialFetchRef.current = true;
      userIdRef.current = user.id;
      fetchUserPoints();
      fetchProducts();
    }
  }, [user, fetchUserPoints, fetchProducts]);

  // Fetch products when filters/pagination change (not on user object updates)
  useEffect(() => {
    if (hasInitialFetchRef.current && userIdRef.current === user?.id) {
      fetchProducts();
    }
  }, [currentPage, searchTerm, selectedCategory, sortBy, fetchProducts, user?.id]);

  // Scroll to top only on pagination or filter changes (not on user object updates)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, searchTerm, selectedCategory, sortBy]);

  // Render loading state
  if (loading && products.length === 0) {
    return (
      <CatalogContainer>
        <ContentWrapper>
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        </ContentWrapper>
      </CatalogContainer>
    );
  }

  return (
    <CatalogContainer>
      <ContentWrapper>
        {/* <Header>
          <Title>🎁 Gift Card Store</Title>
          <Subtitle>
            Browse {totalProducts}+ premium gift cards from top brands. Redeem with your GGTrophy coins instantly!
          </Subtitle>
        </Header> */}

        {/* <PointsDisplay>
          <PointsLabel>Your Balance:</PointsLabel>
          <PointsValue>{userPoints.toLocaleString()} Coins</PointsValue>
        </PointsDisplay> */}

        <FilterBarContainer>
          <MainControls>
            <SearchWrapper>
              <span className="search-icon"><FaSearch /></span>
              <SearchBar
                type="text"
                placeholder="Search catalog..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchWrapper>
            <SortSelect 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">Name (A-Z)</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </SortSelect>
          </MainControls>
          
          <CategoryHeader>
            <FaFilter /> Categories
          </CategoryHeader>
          
          <CategoryScroller>
            {filterOptions.map((option) => (
              <FilterButton
                key={option.key}
                className={selectedCategory === option.key ? 'active' : ''}
                onClick={() => setSelectedCategory(option.key)}
              >
                {option.icon} {option.label} ({option.count})
              </FilterButton>
            ))}
          </CategoryScroller>
        </FilterBarContainer>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {!loading && (
          (selectedCategory === 'wishlist' && wishlistProducts.length === 0) ||
          (viewMode === 'brands' && displayedBrandGroups.length === 0) ||
          (viewMode === 'products' && selectedBrand?.products?.length === 0)
        ) ? (
          <EmptyState>
            😕 No gift cards found. Try adjusting your filters or search terms.
          </EmptyState>
        ) : viewMode === 'brands' ? (
          <>
            <ResultsInfo>
              Showing {displayedBrandGroups.length} brands with {displayedBrandProductCount} total gift cards
            </ResultsInfo>
            
            <ProductGrid>
            {displayedBrandGroups.map((group, index) => {
              const isFlipped = flippedBrands.has(group.brand);
              
              return (
                <BrandCardContainer key={group.brand}>
                  <BrandCardInner $flipped={isFlipped}>
                    {/* Front of Card */}
                    <BrandCardFront onClick={(e) => handleBrandClick(group, e)}>
                      {group.hasDiscount && (
                        <DiscountBadge>
                           -{group.maxDiscount}%
                        </DiscountBadge>
                      )}
                      <FavoriteButton
                        $isFavorite={isFavorite(group.brand)}
                        disabled={loadingFavorites}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(group);
                        }}
                        title={isFavorite(group.brand) ? 'Remove from favorites' : 'Add to favorites (max 3)'}
                      >
                        {isFavorite(group.brand) ? '❤️' : '🤍'}
                      </FavoriteButton>
                      <ProductImage 
                        $hasImage={!!group.image}
                      >
                        {group.image && (
                          <img 
                            src={group.image} 
                            alt={group.brand}
                          />
                        )}
                      </ProductImage>
                      
                      <ProductDetails style={{ bottom: '35px', paddingBottom: '0' }}>
                        <ProductName style={{ fontSize: '1.2rem', marginBottom: '0' }}>{group.brand}</ProductName>
                      </ProductDetails>
                      
                      <BrandOptionsFooter className="brand-footer">
                        {group.count} {group.count === 1 ? 'Option' : 'Options'} Available
                      </BrandOptionsFooter>
                    </BrandCardFront>
                    
                    {/* Back of Card - Flags */}
                    {group.regionImages && group.regionImages.length > 0 && (
                      <BrandCardBack>
                        <CloseFlipButton onClick={(e) => {
                          e.stopPropagation();
                          setFlippedBrands(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(group.brand);
                            return newSet;
                          });
                        }}><FaTimes /></CloseFlipButton>
                        <FlipCardTitle>{group.brand}</FlipCardTitle>
                        <FlipCardFlagsGrid>
                          {group.regionImages.map((region, idx) => (
                            <FlipCardFlag 
                              key={idx}
                              onClick={() => handleCountrySelect(region, group)}
                            >
                              <img src={region.url} alt={region.name} />
                              <FlipCardFlagName>{region.name}</FlipCardFlagName>
                            </FlipCardFlag>
                          ))}
                        </FlipCardFlagsGrid>
                      </BrandCardBack>
                    )}
                  </BrandCardInner>
                  
                  {/* Flags Row Below Card */}
                  {group.regionImages && group.regionImages.length > 0 && (
                    <FlagsRowContainer>
                      {group.regionImages.slice(0, 3).map((region, idx) => (
                        <SmallFlag 
                          key={idx}
                          className="small-flag"
                          title={region.name}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCountrySelect(region, group);
                          }}
                        >
                          <img src={region.url} alt={region.name} />
                        </SmallFlag>
                      ))}
                      {group.regionImages.length > 3 && (
                        <FlagCounter title={`${group.regionImages.length - 3} more regions`}>
                          +{group.regionImages.length - 3}
                        </FlagCounter>
                      )}
                    </FlagsRowContainer>
                  )}
                </BrandCardContainer>
              );
            })}
          </ProductGrid>
          </>
        ) : (
          <>
            {selectedCategory !== 'wishlist' && (
              <div style={{ marginBottom: '1rem' }}>
                <PaginationButton onClick={handleBackToBrands}>
                  ← Back to Brands
                </PaginationButton>
              </div>
            )}
            
            <ResultsInfo>
              {selectedBrand?.brand} - Showing {selectedBrand?.count} gift card{selectedBrand?.count !== 1 ? 's' : ''}
            </ResultsInfo>
            
            <ProductGrid>
            {sortedSelectedBrandProducts.map((product, index) => {
              const hasImage = product.image && imageLoadStates[product.id] !== 'error';
              const isLoading = imageLoadStates[product.id] === 'loading';
              const inStock = isProductInStock(product); // Use isProductInStock instead of isProductAvailable
              const wishlisted = isInWishlist(product.id);
              
              return (
                <ProductCard
                  key={product.id}
                  data-product-id={product.id}
                  onClick={() => {
                    if (inStock) {
                      trackItemClick(product);
                      setSelectedProduct(product);
                    }
                  }}
                  style={{ 
                    cursor: inStock ? 'pointer' : 'default',
                    opacity: inStock ? 1 : 0.7,
                    position: 'relative',
                    transition: 'box-shadow 0.3s ease'
                  }}
                >
                  {!inStock && (
                    <>
                      <OutOfStockBadge>Out of Stock</OutOfStockBadge>
                      <WishlistButton
                        $isWishlisted={wishlisted}
                        disabled={loadingWishlist}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleWishlist(product);
                        }}
                        title={wishlisted ? 'Remove from wishlist' : 'Add to wishlist - We\'ll reorder this!'}
                      >
                        {wishlisted ? '✓' : '+'}
                      </WishlistButton>
                    </>
                  )}

                  {/* Show discount badge if product has discount */}
                  {inStock && product.hasDiscount && (
                    <DiscountBadge>
                      -{product.discountPercent}%
                    </DiscountBadge>
                  )}
                  
                  <ProductImage 
                    $hasImage={hasImage}
                    className={isLoading ? 'loading' : ''}
                  >
                    {product.image && imageLoadStates[product.id] !== 'error' && (
                      <img 
                        src={product.image} 
                        alt={product.displayName || product.productName}
                        onLoad={() => handleImageLoad(product.id)}
                        onError={() => handleImageError(product.id)}
                      />
                    )}
                  </ProductImage>
                  
                  <ProductDetails>
                    {/* Regional Flag */}
                    {product.regionImage && product.regionName && (
                      <RegionalFlagsContainer>
                        <RegionalFlag $index={0} title={product.regionName}>
                          <img src={product.regionImage} alt={product.regionName} />
                        </RegionalFlag>
                      </RegionalFlagsContainer>
                    )}
                    
                    <ProductName>
                      {product.displayName || product.productName || product.merchantName}
                    </ProductName>
                    
                    {/* Show discounted price with original price if discount exists */}
                    {inStock && product.hasDiscount ? (
                      <DiscountedPriceContainer>
                        <OriginalPrice>
                          {product.originalSellingPrice?.toLocaleString()} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '16px', height: '16px', verticalAlign: 'text-bottom' }} />
                        </OriginalPrice>
                        <DiscountedPrice>
                          {product.sellingPrice?.toLocaleString()} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '16px', height: '16px', verticalAlign: 'text-bottom' }} />
                        </DiscountedPrice>
                      </DiscountedPriceContainer>
                    ) : (
                      <>
                        {inStock && <ProductPrice>{product.sellingPrice?.toLocaleString()} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '16px', height: '16px', verticalAlign: 'text-bottom' }} /></ProductPrice>}
                        {!inStock && <ProductPrice style={{ color: '#ff6b6b' }}>Out of Stock</ProductPrice>}
                      </>
                    )}
                  </ProductDetails>
                  
                  {/* <ProductCounter>
                    {product.available}/{product.faceValue || product.available}
                  </ProductCounter> */}
                </ProductCard>
              );
            })}
          </ProductGrid>

          {/* Pagination Controls */}
          {totalPages > 1 && selectedBrand?.brand !== 'Wishlist' && (
            <PaginationContainer>
              <PaginationButton
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                ⏮️ First
              </PaginationButton>
              
              <PaginationButton
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              >
                ⬅️ Prev
              </PaginationButton>

              {/* Page numbers */}
              {[...Array(totalPages)].map((_, index) => {
                const pageNum = index + 1;
                // Show current page, first, last, and 2 pages around current
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  Math.abs(pageNum - currentPage) <= 1
                ) {
                  return (
                    <PaginationButton
                      key={pageNum}
                      $active={pageNum === currentPage}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </PaginationButton>
                  );
                } else if (
                  pageNum === currentPage - 2 ||
                  pageNum === currentPage + 2
                ) {
                  return <PaginationInfo key={pageNum}>...</PaginationInfo>;
                }
                return null;
              })}

              <PaginationButton
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              >
                Next ➡️
              </PaginationButton>
              
              <PaginationButton
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                Last ⏭️
              </PaginationButton>
            </PaginationContainer>
          )}
          </>
        )}

        {/* Confirmation Modal */}
        {selectedProduct && !showRedemptionModal && isProductAvailable(selectedProduct) && (
          <Modal onClick={() => setSelectedProduct(null)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalTitle>✅ Confirm Redemption</ModalTitle>
              <ModalText style={{ color: theme.colors.success, marginBottom: theme.spacing.md }}>
                ✓ Gift card validated and available
              </ModalText>
              <ModalText>
                Are you sure you want to redeem:
              </ModalText>
              <ModalText style={{ fontSize: theme.typography.fontSizes.lg, fontWeight: theme.typography.fontWeights.bold }}>
                {selectedProduct.productName || selectedProduct.merchantName}
              </ModalText>
              <ModalText>
                Cost: <strong>{selectedProduct.sellingPrice?.toLocaleString()} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '16px', height: '16px', verticalAlign: 'text-bottom' }} /></strong>
              </ModalText>
              <ModalText>
                Remaining after: <strong>{(userPoints - selectedProduct.sellingPrice).toLocaleString()} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '16px', height: '16px', verticalAlign: 'text-bottom' }} /></strong>
              </ModalText>
              <ButtonGroup>
                <Button onClick={() => setSelectedProduct(null)}>
                  Cancel
                </Button>
                <Button
                  $primary
                  disabled={redeeming}
                  onClick={handleRedeem}
                >
                  {redeeming ? 'Processing...' : 'Confirm Redemption'}
                </Button>
              </ButtonGroup>
            </ModalContent>
          </Modal>
        )}

        {/* Success Modal */}
        {showRedemptionModal && redemptionResult && (
          <Modal onClick={() => setShowRedemptionModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalTitle>🎉 Redemption Successful!</ModalTitle>
              <ModalText>
                Your gift card has been successfully redeemed and sent to your inventory!
              </ModalText>
              <ModalText style={{ color: theme.colors.primary, fontWeight: '600', marginTop: theme.spacing.md }}>
                📦 Go to your Inventory to reveal your redemption code
              </ModalText>
              <ModalText>
                Order ID: {redemptionResult.orderName}
              </ModalText>
              <ModalText>
                Remaining Balance: <strong>{(redemptionResult.newTotalPoints ?? redemptionResult.remainingPoints)?.toLocaleString()} <img src='images/ggcoin.svg' alt='GG Coin' style={{ width: '16px', height: '16px', verticalAlign: 'text-bottom' }} /></strong>
              </ModalText>
              <ButtonGroup>
                <Button $primary onClick={() => setShowRedemptionModal(false)}>
                  Close
                </Button>
              </ButtonGroup>
            </ModalContent>
          </Modal>
        )}
      </ContentWrapper>
    </CatalogContainer>
  );
};

export default Catalog;
